import {
  ActionLabel,
  getPlaceholder,
  getPlaceholderForConversionReportType,
  getPlaceholderForCustomReportType,
  getStatus,
  getStatusForConversionReportType,
  getStatusForCustomReportType,
  isFromScratch,
  isMinimalFiling,
  isNilReturnPeriod,
  NilReturnCell,
  statusKeys
} from '../../../data';

import { ClipLoader } from 'react-spinners';
import OutlinedNavigateIcon from '../../../../../../assets/icons/navigate-outlined.svg?react';
import { useAutomateExtraction } from '../../../../../../services/automation.service';
import toast from 'react-hot-toast';
import { useEffect, useMemo, useState } from 'react';
import { REPORT_TYPES } from '../../../../../../consts/reportTypes';
import { extractionNotStarted } from '../../../../../../utils/periodProgress';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa6';
import { PermissionGuard } from '../../../../../../components/PermissionGuard';
import { Tooltip } from '@mantine/core';
import { extractApiError } from '../../../../../../utils';

export const ExtractionColumn = ({
  period,
  isLoadingPeriods,
  onOpenDrawer,
  refetch,
  workflowId,
  reportType = REPORT_TYPES.ANNEX_IV,
  canManageWorkflow = true,
  onOpenInitialExtractionDrawer,
  /* The redesigned strip captions the stage already, so the cell drops the stage from its own
     label. Off by default — the classic column keeps every string it has. */
  compactLabels = false
}) => {
  const navigate = useNavigate();
  const status = useMemo(() => {
    if (reportType === REPORT_TYPES.CONVERSION) return getStatusForConversionReportType(period);
    else if (reportType === REPORT_TYPES.CUSTOM) return getStatusForCustomReportType(period);

    return getStatus(period, reportType);
  }, [reportType, period]);
  const isScratch = isFromScratch(period);
  const placeholder = useMemo(() => {
    if (reportType === REPORT_TYPES.CONVERSION)
      return getPlaceholderForConversionReportType(period, reportType);
    else if (reportType === REPORT_TYPES.CUSTOM)
      return getPlaceholderForCustomReportType(period, reportType);

    return getPlaceholder(period, reportType);
  }, [reportType, period]);
  const automateExtraction = useAutomateExtraction();
  const activeExtractionsCount =
    (period?.extraction_stats?.in_progress_extractions_number || 0) +
    (period?.extraction_stats?.queued_extractions_number || 0);

  const [allowToShowLoadingPeriods, setAllowToShowLoadingPeriods] = useState(false);

  useEffect(() => {
    if (!isLoadingPeriods) setAllowToShowLoadingPeriods(false);
  }, [isLoadingPeriods]);

  const onAutoExtract = () => {
    if (isFromScratch(period)) {
      navigate(`/data-extraction/${workflowId}/${period.id}?tab=extraction`);
      return;
    }

    automateExtraction.mutate(
      {
        period_id: period?.id
      },
      {
        onSuccess: () => {
          refetch();
          setAllowToShowLoadingPeriods(true);
          toast.success('Automated Extraction have been successfully applied', {
            position: 'bottom-center'
          });
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to start automatic extraction.'));
        }
      }
    );
  };
  const onClick = () => {
    if (period.template_type === 'sratch') {
      onOpenInitialExtractionDrawer(period?.id);
      return;
    }

    onOpenDrawer(period?.id);
  };

  const onOpenIssues = () => {
    onOpenDrawer(period?.id);
  };

  const isNextStatus =
    status === statusKeys.EXTRACTION ||
    status === statusKeys.TRANSFORMATION ||
    status === statusKeys.REPORT ||
    status === statusKeys.APPROVAL ||
    status === statusKeys.SUBMISSION ||
    status === statusKeys.END;

  const hasWarningsOrErrors =
    !!period?.extraction_stats?.failed_extractions_number ||
    !!period?.extraction_stats?.warned_extractions_number ||
    activeExtractionsCount > 0 ||
    !!period?.extraction_stats?.not_completed_extractions_number;

  const notStarted = extractionNotStarted(period);

  const isNotCompleted =
    period?.extraction_stats?.failed_extractions_number === 0 &&
    period?.extraction_stats?.warned_extractions_number === 0 &&
    activeExtractionsCount === 0 &&
    period?.extraction_stats?.successful_extractions_number === 0 &&
    period?.extraction_stats?.not_completed_extractions_number === 0;

  if (automateExtraction.isLoading)
    return <ActionLabel style={{ width: 150 }} placeholder="Extracting..." />;

  if (isLoadingPeriods && allowToShowLoadingPeriods)
    return <ActionLabel style={{ width: 150 }} placeholder="Extracting..." />;

  if (isMinimalFiling(period)) return <ActionLabel style={{ width: 150 }} placeholder="N/A" />;

  if (reportType === REPORT_TYPES.ANNEX_IV && isNilReturnPeriod(period))
    return (
      <NilReturnCell id={`nil-return-extraction-${period.id}`} message="no extraction required" />
    );

  /* The run has started and the backend has not counted anything yet. `isNotCompleted` means every
     counter is zero — `not_completed_extractions_number` included — so this branch used to build its
     label out of that zero and could only ever read "0 not extracted": a number that never changed,
     over a period where nothing had failed to extract. It is the waiting state, and it says so, in
     the same words the two branches above already use for waiting. Counts take over on their own
     once any counter moves: `isNotCompleted` goes false and the tally below draws them. */
  if (period.extraction_progress_status === 'In progress' && isNotCompleted) {
    return <ActionLabel style={{ width: 150 }} placeholder="Extracting..." />;
  }

  /* `notStarted` is measured — nothing extracted, and sheets mapped that could be. It offers Start
     on a period whose rolled-up status has moved past Extraction without it ever running, which is
     what had this cell reading "Finished" beside an extraction list holding none. Same rule as the
     in-period stepper and the periods list, so the three agree. */
  if (
    (status === statusKeys.EXTRACTION || notStarted) &&
    (!hasWarningsOrErrors || period?.extraction_progress_status === 'Not started' || notStarted)
  )
    return (
      <PermissionGuard allowed={canManageWorkflow}>
        <Tooltip
          label="You do not have permission to start extraction"
          disabled={canManageWorkflow}
        >
          <ActionLabel
            style={{ width: 150 }}
            label={compactLabels ? 'Start' : 'Start Extraction'}
            rightIcon={<OutlinedNavigateIcon />}
            onClick={onAutoExtract}
            color={!canManageWorkflow ? 'gray' : undefined}
            disabled={!canManageWorkflow}
          />
        </Tooltip>
      </PermissionGuard>
    );

  if (hasWarningsOrErrors)
    return (
      <>
        {period?.extraction_stats?.successful_extractions_number > 0 && (
          <ActionLabel
            label={period?.extraction_stats?.successful_extractions_number + ' successful'}
            color="#40C057"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}

        {period?.extraction_stats?.failed_extractions_number > 0 && (
          <ActionLabel
            label={period?.extraction_stats?.failed_extractions_number + ' issues'}
            color="#FA5252"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}

        {period?.extraction_stats?.warned_extractions_number > 0 && (
          <ActionLabel
            label={period?.extraction_stats?.warned_extractions_number + ' issues'}
            color="#FD7E14"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}

        {period?.extraction_stats?.not_completed_extractions_number > 0 && (
          <ActionLabel
            label={period?.extraction_stats?.not_completed_extractions_number + ' not extracted'}
            color="#7b7b7b"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}

        {activeExtractionsCount > 0 && (
          <ActionLabel
            label={activeExtractionsCount + ' extracting'}
            color="#228BE6"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}
      </>
    );

  if (
    period.extraction_progress_status === 'In progress' &&
    !isScratch &&
    !period?.extraction_stats?.successful_extractions_number
  )
    return (
      <ActionLabel
        style={{ width: 150 }}
        label="In progress"
        rightIcon={<ClipLoader size={16} color="#228BE6" />}
        onClick={onClick}
      />
    );

  if (
    (period.extraction_progress_status === 'Completed' ||
      isScratch ||
      period?.extraction_stats?.successful_extractions_number > 0) &&
    isNextStatus
  )
    return (
      <ActionLabel
        style={{ width: 150 }}
        label="Finished"
        color="#40C057"
        onClick={onClick}
        rightIcon={<FaArrowRight />}
      />
    );

  return <ActionLabel placeholder={placeholder} />;
};
